import { z } from 'zod';
import { CalendarDateOnlySchema, LowercaseUuidSchema, PaginationQuerySchema, createPaginatedResponseSchema } from './common.js';
// =============================================================================
// Bank feeds (super-admin surface)
// =============================================================================
export const LinkBankFeedInputSchema = z.strictObject({
    bankConnectionId: LowercaseUuidSchema,
    /** The bank's id for the account, from the connection's provider-account list. */
    providerAccountId: z.string().min(1).max(128),
    /** The first day whose transactions become lines. */
    startDate: CalendarDateOnlySchema,
    /**
     * Accept lines from another source dated on or after the start, for the
     * first sync to pair with the bank's rows, instead of refusing the link.
     * The start must then be within the last week.
     */
    adoptExistingLines: z.boolean().default(false),
    idempotencyKey: z.string().min(1).max(128),
});
export const BankFeedSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    bankConnectionId: z.string(),
    bankAccountId: z.string(),
    providerAccountId: z.string(),
    startDate: CalendarDateOnlySchema,
    createdByUserId: z.string(),
    failureAlertedAt: z.string().datetime().nullable(),
    archivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BankFeedResponseSchema = z.object({ data: BankFeedSchema });
export const BankFeedListResponseSchema = z.object({ data: z.array(BankFeedSchema) });
export const BankFeedSyncRunStatusSchema = z.enum(['SUCCEEDED', 'FAILED']);
export const BankFeedSyncErrorCodeSchema = z.enum([
    'CREDENTIAL_REJECTED',
    'UPSTREAM_FAILURE',
    'LEDGER_NOT_LINKED',
    'LINE_REJECTED',
    'POSSIBLE_DUPLICATE',
    'LINE_IN_PROGRESS',
    'INTERNAL',
]);
/** The bank's id for one transaction, as a request takes it. Opaque. */
export const ProviderTransactionIdSchema = z.string().min(1).max(128);
/**
 * A bank row the feed held back because a line already on the bank account
 * looks like it. Opaque ids, dates and amounts only: never the description.
 */
export const HeldBankFeedRowSchema = z.object({
    providerTransactionId: z.string(),
    date: CalendarDateOnlySchema,
    postingDate: CalendarDateOnlySchema,
    amountMinorUnits: z.number().int(),
    postingOrder: z.number().int(),
    /** The lines with the same amount and date, none with the same description. */
    candidateLineIds: z.array(z.string()),
});
/** One sync of a feed. Counts and held rows only: no description from the bank. */
export const BankFeedSyncRunSchema = z.object({
    id: z.string(),
    bankFeedId: z.string(),
    windowFrom: CalendarDateOnlySchema,
    windowTo: CalendarDateOnlySchema,
    status: BankFeedSyncRunStatusSchema,
    errorCode: BankFeedSyncErrorCodeSchema.nullable(),
    settledSeenCount: z.number().int(),
    linesCreatedCount: z.number().int(),
    /** Settled rows Bill refused to write; retried on the next sync. */
    linesRejectedCount: z.number().int(),
    /** Rows recorded against a line already on the bank account instead of a new line. */
    linesPairedCount: z.number().int(),
    /** Rows held as a possible duplicate; each is in `heldRows` until an operator resolves it. */
    linesHeldCount: z.number().int(),
    heldRows: z.array(HeldBankFeedRowSchema),
    driftCount: z.number().int(),
    /** Rows the bank returned that were not admitted, by reason. */
    withheldCounts: z.object({}).catchall(z.number().int()),
    /** How many calls the run made to the bank. */
    apiCallCount: z.number().int(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime(),
});
export const BankFeedSyncRunListQuerySchema = z.strictObject(PaginationQuerySchema.shape);
export const BankFeedSyncRunListResponseSchema = createPaginatedResponseSchema(BankFeedSyncRunSchema);
// A held row is released one of two ways: recorded against a line already on
// the bank account, or approved to import as a new line on the next sync.
const PairHeldRowSchema = z.strictObject({
    action: z.literal('pair'),
    bankStatementLineId: LowercaseUuidSchema,
});
const ImportHeldRowSchema = z.strictObject({ action: z.literal('import') });
export const HeldBankFeedRowResolutionSchema = z.discriminatedUnion('action', [PairHeldRowSchema, ImportHeldRowSchema]);
const idempotencyKeyShape = { idempotencyKey: z.string().min(1).max(128) };
export const ResolveHeldBankFeedRowInputSchema = z.discriminatedUnion('action', [
    PairHeldRowSchema.extend(idempotencyKeyShape),
    ImportHeldRowSchema.extend(idempotencyKeyShape),
]);
export const BankFeedTransactionStateSchema = z.enum(['IMPORTED', 'PAIRED', 'IMPORT_APPROVED']);
/** The feed transaction a resolution wrote. Ids, dates and amounts only. */
export const BankFeedTransactionSchema = z.object({
    id: z.string(),
    bankFeedId: z.string(),
    providerTransactionId: z.string(),
    bankStatementLineId: z.string().nullable(),
    state: BankFeedTransactionStateSchema,
    date: CalendarDateOnlySchema,
    postingDate: CalendarDateOnlySchema,
    amountMinorUnits: z.number().int(),
});
export const BankFeedTransactionResponseSchema = z.object({ data: BankFeedTransactionSchema });
